import assertStatus from "@mittwald/api-client/dist/types/assertStatus";
import type { MittwaldApi } from "../../api/Mittwald";
import { mittwaldApi } from "../../api/Mittwald";
import { openFileInNewTab } from "../../lib/openFileInNewTab";

export type ContributorApiData =
  MittwaldApi.Components.Schemas.De_Mittwald_V1_Marketplace_Contributor;

export type OwnContributorApiData =
  MittwaldApi.Components.Schemas.De_Mittwald_V1_Marketplace_OwnContributor;

export type ContributorIncomingInvoicesResponseData =
  MittwaldApi.Paths.V2_Contributors_ContributorId_Invoices_Incoming.Get.Responses.$200.Content.Application_Json;

export type ContributorOnbehalfInvoicesResponseData =
  MittwaldApi.Paths.V2_Contributors_ContributorId_Invoices_Outgoing.Get.Responses.$200.Content.Application_Json;

export type ContributorReceiptGetFileAccessTokenResponseData =
  MittwaldApi.Paths.V2_Contributors_ContributorId_Invoices_Incoming_ContributorReceiptId_File_Access_Token.Get.Responses.$200.Content.Application_Json;

export type ContributorImprintData =
  MittwaldApi.Components.Schemas.De_Mittwald_V1_Marketplace_ContributorImprint;

class Contributor {
  public readonly data: ContributorApiData | OwnContributorApiData;
  public readonly id: string;
  public readonly customerId: string;
  public readonly description?: string;
  public readonly email?: string;
  public readonly logoRefId?: string;
  public readonly name: string;
  public readonly phone?: string;
  public readonly contributorNumber?: string;
  public readonly imprint?: ContributorImprintData;
  public readonly state: string;
  public readonly url?: string;

  public constructor(data: ContributorApiData) {
    this.data = Object.freeze(data);
    this.customerId = data.customerId;
    this.id = data.id;
    this.description = data.description;
    this.email = data.email;
    this.logoRefId = data.logoRefId;
    this.name = data.name;
    this.imprint = data.imprint;
    this.phone = data.phone;
    this.state = data.state;
    this.url = data.url;
    if ("contributorNumber" in data) {
      this.contributorNumber = data.contributorNumber as string;
    }
  }

  public static useLoadById(contributorId: string): Contributor {
    const data = mittwaldApi.extensionGetContributor
      .getResource({ path: { contributorId } })
      .useWatchData();

    return new Contributor(data);
  }

  public static useLoadAllIncomingInvoices(
    contributorId: string,
  ): ContributorIncomingInvoicesResponseData {
    const data = mittwaldApi.contributorListIncomingInvoices
      .getResource({ path: { contributorId } })
      .useWatchData({ optional: true });

    return data ?? [];
  }

  public static async getStripeContributorUrl(
    contributorId: string,
  ): Promise<string | undefined> {
    const data = await mittwaldApi.contributorGetLoginLink.request({
      path: { contributorId },
    });

    assertStatus(data, 200);

    return data.content.url;
  }

  public static useLoadAllOnbehalfInvoices(
    contributorId: string,
  ): ContributorOnbehalfInvoicesResponseData {
    const data = mittwaldApi.contributorListOnbehalfInvoices
      .getResource({ path: { contributorId } })
      .useWatchData({ optional: true });

    return data ?? [];
  }

  public static async getInvoicePdfContent(
    contributorId: string,
    contributorReceiptId: string,
    fileId: string,
  ): Promise<any> {
    const tokenResponse =
      await mittwaldApi.contributorReceiptGetFileAccessToken.request({
        path: {
          contributorId,
          contributorReceiptId,
        },
      });

    assertStatus(tokenResponse, 200);

    const fileResponse = await mittwaldApi.fileGetFile.request({
      path: { fileId },
      header: {
        Accept: "text/plain;base64",
        Token: tokenResponse.content.accessToken,
        "x-access-token": undefined,
      },
    });
    assertStatus(fileResponse, 200);

    return fileResponse.content;
  }

  public static async downloadInvoice(
    contributorId: string,
    contributorReceiptId: string,
    fileId: string,
    webLink?: string,
  ): Promise<void> {
    if (webLink) {
      window.open(webLink, "_blank");
      return;
    }

    const invoiceContent = await this.getInvoicePdfContent(
      contributorId,
      contributorReceiptId,
      fileId,
    );

    openFileInNewTab({
      id: fileId,
      content: invoiceContent,
      name: contributorReceiptId,
      type: "application/pdf",
    });
  }

  public static useTryLoadById(
    contributorId: string | undefined,
  ): Contributor | undefined {
    const data = mittwaldApi.extensionGetContributor
      .getResource(contributorId ? { path: { contributorId } } : null)
      .useWatchData({ optional: true });

    return data ? new Contributor(data) : undefined;
  }

  public static async loadById(contributorId: string): Promise<Contributor> {
    const data = await mittwaldApi.extensionGetContributor
      .getResource({ path: { contributorId } })
      .load();

    return new Contributor(data);
  }
}

export default Contributor;
